import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../withTypes';
import { Status, STATUSES } from '@/types/store';
import { RootState } from '..';
import { ModelSelectionTask } from '@/types/assignments';
import {
  BOOKMARK_TASK,
  CALCULATED_MODEL_QUERY,
  FETCH_MODEL_SELECTION_TASKS,
  UPDATE_SELECTED_MODEL_MUTATION,
  UPDATE_TASK,
  UPDATE_TASK_MODEL_SELECTION,
} from '@/graphql/queries/modelSelection';
import { graphqlMutation, graphqlRequest, addRejectedToast, showRejectedToast } from '@/api/graphqlClient';
import {
  CalculatedModelQuery,
  CalculatedModelQueryVariables,
  ModelSelectionBookmarkTaskMutation,
  ModelSelectionBookmarkTaskMutationVariables,
  ModelSelectionUpdateTaskMutation,
  ModelSelectionUpdateTaskMutationVariables,
  TaskModelSelectionData,
  TaskModelSelectionOptionData,
  TaskWithData,
  UpdateSelectedModelForProjectMutation,
  UpdateSelectedModelForProjectMutationVariables,
  UpdateTaskInput,
  UpdateTaskModelSelectionMutation,
  UpdateTaskModelSelectionMutationVariables,
  UpsertTaskNoteMutation,
  UpsertTaskNoteMutationVariables,
} from '@/graphql/types';
import { UPSERT_TASK_NOTE } from '@/graphql/queries/tasks';
import { errorMessages } from '@/utils/errorMessages';
import { getNotes, showToast } from '@/utils/utils';
import i18n from '@i18n/i18n.config';

export interface ModelSelectionState {
  taskList: ModelSelectionTask[];
  selectedTaskId: number | null;
  status: Status;
}

interface UpsertNote {
  taskId: number;
  userId: number;
  noteText: string;
  noteId?: number | null;
}

const initialState: ModelSelectionState = {
  taskList: [],
  selectedTaskId: null,
  status: STATUSES.IDLE,
};

/* THUNKS */
export const fetchModelSelectionTaskList = createAppAsyncThunk(
  'assignments/fetchModelSelectionTaskList',
  async (assignmentId: number) => {
    const data = await graphqlRequest<{ assignmentId: number }, { tasksPerAssignment: TaskWithData[] }>(
      FETCH_MODEL_SELECTION_TASKS,
      { assignmentId },
      'no-cache',
    );

    const filteredData = data.tasksPerAssignment.filter(
      (task: TaskWithData) => task.data?.__typename === 'TaskModelSelectionData',
    );

    if (filteredData.length === 0) {
      showToast(errorMessages.taskNotFound, 'error');
    }

    const mappedData = filteredData.map((task: TaskWithData) => {
      const taskData = task.data as TaskModelSelectionData;

      return {
        id: task.id,
        title: `${i18n.t('bookList.' + taskData.source_sentence?.book_title)} ${taskData.source_sentence?.chapter_title} : ${taskData.source_sentence?.sentence_number}`,
        taskTypeId: task.task_type_id,
        taskDataId: task.task_data_id,
        projectId: task.project_id,
        assignmentId: task.assignment_id,
        isBookmarked: task.is_bookmarked,
        isCompleted: task.status_id === 4,
        selectedAITranslationId: taskData.best_translated_sentence_id || null,
        aiTranslations: taskData.options.map((option: TaskModelSelectionOptionData) => ({
          id: option.id,
          model_id: option.model_id,
          translated_sentence_id: option.translated_sentence_id,
          translation: option.translated_sentence?.sentence_text || '',
        })),
        sourceText: taskData.source_sentence?.sentence_text || '',
        notesList: getNotes(task.notes),
        bookId: taskData.source_sentence?.book_id,
      } as ModelSelectionTask;
    });

    return mappedData;
  },
);

export const calculatedModelForProject = createAppAsyncThunk(
  'tasks/calculatedModelForProject',
  async ({ projectId, levelId }: { projectId: number, levelId: number }) => {
    const data = await graphqlRequest<CalculatedModelQueryVariables, CalculatedModelQuery>(
      CALCULATED_MODEL_QUERY,
      { projectId, levelId },
    );

    if (!data.calculatedModelForProject) {
      showToast(errorMessages.fetchingCalculatedModel, 'error');
      return null;
    }

    return data.calculatedModelForProject.calculatedModelId;
  },
);

export const updateSelectedModelForProject = createAppAsyncThunk(
  'tasks/updateSelectedModelForProject',
  async ({ projectId, modelId }: { projectId: number, modelId: string }) => {
    const data = await graphqlMutation<UpdateSelectedModelForProjectMutationVariables, UpdateSelectedModelForProjectMutation>(
      UPDATE_SELECTED_MODEL_MUTATION,
      { projectId, modelId },
    );
    return data.updateSelectedModelForProject;
  },
);

export const updateTask = createAppAsyncThunk(
  'tasks/updateTask',
  async ({ id, task }: { id: number, task: UpdateTaskInput }) => {
    const data = await graphqlMutation<ModelSelectionUpdateTaskMutationVariables, ModelSelectionUpdateTaskMutation>(
      UPDATE_TASK,
      { id, task },
    );
    return data.updateTask;
  },
);

export const bookmarkModelSelectionTask = createAppAsyncThunk(
  'assignments/bookmarkModelSelectionTask',
  async ({ id, isBookmarked }: { id: number, isBookmarked: boolean }) => {
    const data = await graphqlMutation<ModelSelectionBookmarkTaskMutationVariables, ModelSelectionBookmarkTaskMutation>(
      BOOKMARK_TASK,
      { id, is_bookmarked: isBookmarked },
    );
    return data;
  },
);

export const updateTaskModelSelection = createAppAsyncThunk(
  'assignments/updateTaskModelSelection',
  async ({ taskDataId, bestTranslatedSentenceId, modelId }: { taskDataId: number, bestTranslatedSentenceId: number, modelId: string }) => {
    const data = await graphqlMutation<UpdateTaskModelSelectionMutationVariables, UpdateTaskModelSelectionMutation>(
      UPDATE_TASK_MODEL_SELECTION,
      { id: taskDataId, bestTranslatedSentenceId, modelId },
    );
    return data.updateTaskModelSelection;
  },
);

export const upsertNote = createAppAsyncThunk(
  'assignments/upsertNote',
  async ({ taskId, userId, noteText, noteId }: UpsertNote) => {
    const data = await graphqlMutation<UpsertTaskNoteMutationVariables, UpsertTaskNoteMutation>(
      UPSERT_TASK_NOTE,
      { taskId, userId, noteText, noteId },
    );

    return {
      data,
      taskId,
    };
  },
);

export const modelSelectionReducer = createSlice({
  name: 'modelSelection',
  initialState,
  reducers: {
    setSelectedTaskId: (state, action: PayloadAction<number>) => {
      state.selectedTaskId = action.payload;
    },
    selectNextTaskId: (state, action: PayloadAction<{ currentSelectedTaskId?: number, taskListToUse: ModelSelectionTask[] }>) => {
      const { currentSelectedTaskId, taskListToUse } = action.payload;
      const selectedIndex = taskListToUse.findIndex(obj => obj.id === currentSelectedTaskId);
      if (selectedIndex >= 0 && taskListToUse[selectedIndex + 1]) {
        state.selectedTaskId = taskListToUse[selectedIndex + 1].id;
      }
    },
    setSelectedAITranslationId: (state, action: PayloadAction<number>) => {
      const taskIndex = state.taskList.findIndex(obj => obj.id === state.selectedTaskId);
      if (taskIndex >= 0) {
        state.taskList[taskIndex].selectedAITranslationId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchModelSelectionTaskList.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchModelSelectionTaskList.fulfilled, (state, action) => {
      state.taskList = action.payload;
      state.selectedTaskId
        = action.payload.find((task: ModelSelectionTask) => !task.isCompleted)?.id
          ?? action.payload[0]?.id
          ?? null;
      state.status = STATUSES.FULFILLED;
    });
    builder.addCase(fetchModelSelectionTaskList.rejected, (state, action) => {
      state.status = STATUSES.REJECTED;
      state.taskList = initialState.taskList;
      state.selectedTaskId = initialState.selectedTaskId;
      showRejectedToast(action, errorMessages.fetchingTasks);
    });
    builder.addCase(bookmarkModelSelectionTask.fulfilled, (state, action) => {
      const { id, is_bookmarked } = action.payload.bookmarkTask;
      const taskIndex = state.taskList.findIndex(task => task.id === id);
      if (taskIndex >= 0) {
        state.taskList[taskIndex].isBookmarked = is_bookmarked ?? false;
      }
    });
    addRejectedToast(builder, bookmarkModelSelectionTask, errorMessages.bookmarkingTask);
    builder.addCase(updateTask.fulfilled, (state, action: PayloadAction<{ id: number, status_id: number }>) => {
      const { id, status_id } = action.payload; // Correctly destructure `status_id` from the payload
      const taskIndex = state.taskList.findIndex(existingTask => existingTask.id === id);
      if (taskIndex >= 0) {
        state.taskList[taskIndex] = {
          ...state.taskList[taskIndex],
          isCompleted: status_id === 4,
        };
      }
    });
    addRejectedToast(builder, updateTask, errorMessages.updatingTask);
    builder.addCase(updateTaskModelSelection.fulfilled, (state, action) => {
      const { id, bestTranslatedSentenceId } = action.payload;
      const taskIndex = state.taskList.findIndex(task => task.taskDataId === id);
      if (taskIndex >= 0) {
        state.taskList[taskIndex] = {
          ...state.taskList[taskIndex],
          selectedAITranslationId: bestTranslatedSentenceId ?? undefined,
        };
      }
    });
    addRejectedToast(builder, updateTaskModelSelection, errorMessages.updatingTask);
    builder.addCase(upsertNote.fulfilled, (state, action) => {
      const { taskId, data } = action.payload;
      const note = data?.upsertTaskNote;
      if (!note) return;
      const tasks = state.taskList;
      const taskIndex = tasks.findIndex(task => task.id === taskId);

      if (taskIndex >= 0) {
        const noteIndex = tasks[taskIndex].notesList.findIndex(taskNote => taskNote.id === note.id);

        if (noteIndex < 0) {
          tasks[taskIndex].notesList.push(note);
        } else {
          tasks[taskIndex].notesList[noteIndex] = note;
        }
      }
    });
    addRejectedToast(builder, upsertNote, errorMessages.savingNote);
  },
});

export const taskList = (state: RootState) => state.modelSelectionReducer.taskList;

export const getCompletedTasks = createSelector([taskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === true);
});

export const getSelectedTaskById = createSelector(
  [taskList, (_, selectedTaskId: number | null) => selectedTaskId],
  (tasks, selectedTaskId) => {
    return tasks.find((obj: ModelSelectionTask) =>
      obj.id === selectedTaskId,
    );
  },
);

export const getFilteredTaskList = createSelector(
  [taskList],
  tasks =>
    tasks.map(({ id, sourceText, title, aiTranslations, isCompleted, isBookmarked }) => ({
      id,
      content: sourceText,
      title,
      aiTranslations,
      isBookmarked,
      isCompleted,
    })),
);

export const getIncompleteTasks = createSelector([getFilteredTaskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === false);
});

export const getModelSelectionStatus = (state: RootState) => state.modelSelectionReducer?.status;

export const {
  setSelectedTaskId,
  selectNextTaskId,
  setSelectedAITranslationId,
} = modelSelectionReducer.actions;

export default modelSelectionReducer.reducer;
